import type { ComponentType, ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { EngineeringIcon } from 'framework/ui/mui-builder';
import { ShowDate } from 'framework/ui/ui-builder';
import { DATE_FORMAT } from 'framework/utils';
import type { TimelineRecordAuthorProps } from '../types/configTypes';
import type { TimelineItem } from '../types/types';

const rowSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: '30px 1fr',
  columnGap: 1.5,
};

const markerColumnSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const iconCircleSx: SxProps<Theme> = theme => ({
  width: 30,
  height: 30,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: theme.palette.common.scrollbarLightGrey,
  color: theme.palette.common.white,
  stroke: 'currentColor',
  '& svg': {
    fontSize: 18,
  },
});

const connectorLineSx: SxProps<Theme> = theme => ({
  flexGrow: 1,
  width: '1px',
  minHeight: 8,
  backgroundColor: theme.palette.divider,
});

const metaBarSx: SxProps<Theme> = theme => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1,
  minHeight: 30,
  px: 1,
  backgroundColor: theme.palette.background.default,
  borderRadius: 1,
  flexWrap: 'wrap',
});

const contentSx: SxProps<Theme> = {
  px: 2,
  py: 1.5,
};

const recordAuthorSx: SxProps<Theme> = {
  '*': { fontSize: 12 },
};

interface TimelineRecordLayoutProps {
  item: TimelineItem;
  RecordAuthor: ComponentType<TimelineRecordAuthorProps>;
  children: ReactNode;
  actions?: ReactNode;
  isHighlighted?: boolean;
  showRecordMeta?: boolean;
  tag?: ReactNode;
  tagColor?: TimelineItem['tagColor'];
}

const TimelineRecordLayout = ({
  item,
  RecordAuthor,
  children,
  actions,
  isHighlighted = false,
  showRecordMeta = true,
  tag = item.tag,
  tagColor = item.tagColor,
}: TimelineRecordLayoutProps) => {
  return (
    <Box sx={rowSx}>
      <Box sx={markerColumnSx} aria-hidden="true">
        <Box sx={[iconCircleSx, { backgroundColor: item.iconCircleColor }]}>{item.icon ?? <EngineeringIcon />}</Box>
        <Box sx={connectorLineSx} />
      </Box>
      <Box
        aria-current={isHighlighted ? 'true' : undefined}
        sx={{
          minWidth: 0,
          ...(isHighlighted && {
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 1,
          }),
        }}
      >
        <Box sx={[metaBarSx, { backgroundColor: item.headerBgColor }]}>
          <Box>
            {tag && (
              <Typography variant="subtitle2" color={tagColor}>
                {tag}
              </Typography>
            )}
          </Box>
          {showRecordMeta && (
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <RecordAuthor id={item.actor} sx={recordAuthorSx} />
              <ShowDate dateUTC={item.occurredAt} format={DATE_FORMAT.FULL} variant="body2" color="text.secondary" />
              {actions}
            </Stack>
          )}
        </Box>
        <Box sx={contentSx}>{children}</Box>
      </Box>
    </Box>
  );
};

export default TimelineRecordLayout;
